/* ============================================================================
   Stickr Lab — Pricing Engine v1  (assets/sl-pricing.js)

   PRODUCTION SOURCE OF TRUTH for die-cut sticker prices shown in the builder.
   Config-driven: the UI consumes this module; it does NOT own pricing logic.

   What ships here (safe for the browser):
     - Final, public, USD prices only.
     - The approved die-cut selling formula (standard matrix generator).
     - The approved explicit override table (large sizes) — always wins over the formula.
     - A (currently empty) promotional-override layer.

   What NEVER ships here (Rules 6 & 8):
     - Production cost, material cost, margin, the margin floor, or competitor references.
     - No price is computed from production cost in the browser.

   Pricing rules honored:
     1. Prices display to two decimals (see `format`).
     2. Approved table values override generated formula values (precedence below).
     3. Every price is tagged with its source: 'standard' | 'override' | 'promo'
        (and 'interpolated' for custom quantities between tiers).
     4. Material / laminate / border / proof / notes do NOT change die-cut price.
     8. No internal cost/margin/multiplier/competitor data is exposed to customers.

   Source of truth: the Stickr Lab pricing spreadsheet (1×1–6×6 and the unchanged
   200/500/1000 quantities are produced by the approved formula = existing prices).
   The four large-size overrides (7–10 @ 10/25/50/100) are the approved launch policy.

   MINIMUM PRICE PER DESIGN (Ryan, 24 Oct 2026): an individual sticker design is
   never sold for less than $17.99. diecut() charges the GREATER of the calculated
   price and MIN_DESIGN_PRICE, for every shape and cut type priced as an individual
   sticker (die cut, circle/square and their unlocked oval/rectangle, the single
   sticker on backing). A calculated price above it is unchanged. Sheets are NOT
   floored: sheet() is derived from the CALCULATED die-cut price. The $22 promo
   pack is its own variant and is not priced here.

   Sticker Sheets (Launch V1 · D-033): a Sticker Sheet is priced at exactly 8% less
   than the equivalent die-cut order — `sheet = diecut × 0.92`. It is DERIVED from the
   die-cut engine (`sheet()` below), NOT a separate table. Die-cut remains the single
   canonical source; if die-cut prices change, sheet prices stay exactly 8% lower.
   ============================================================================ */
(function (root) {
  'use strict';

  /* ---- Approved die-cut standard matrix (the generator) ------------------
     Individual-sticker selling price = PRICE_BASE × (area/9)^PRICE_EXP × qtyMultiplier
     × (BIG_BUMP if either dimension > BIG_THRESHOLD). Display rule: round to a whole
     dollar, then a .99 ending. This reproduces the existing approved spreadsheet
     prices for every non-overridden cell (any width/height, incl. custom/non-square). */
  var QTY_TIERS   = [10, 25, 50, 100, 200, 500, 1000];
  var QTY_MULTS   = [0.36, 0.54, 0.77, 1.0, 1.6, 3.35, 5.1];
  var PRICE_BASE  = 64.99;
  var PRICE_EXP   = 0.47;
  var BIG_BUMP    = 1.61;
  var BIG_THRESH  = 6;
  var QTY_MIN     = 10;
  var QTY_MAX     = 1000;
  var MIN_DESIGN_PRICE = 17.99;   // per design, individual stickers only (Ryan, 24 Oct 2026)

  function qtyMultiplier(q) {
    q = Math.max(QTY_MIN, q | 0);
    if (q <= QTY_TIERS[0]) return QTY_MULTS[0];
    var last = QTY_TIERS.length - 1;
    if (q >= QTY_TIERS[last]) {
      var slope = (QTY_MULTS[last] - QTY_MULTS[last - 1]) / (QTY_TIERS[last] - QTY_TIERS[last - 1]);
      return QTY_MULTS[last] + (q - QTY_TIERS[last]) * slope;
    }
    for (var i = 0; i < last; i++) {
      if (q >= QTY_TIERS[i] && q <= QTY_TIERS[i + 1]) {
        var t = (q - QTY_TIERS[i]) / (QTY_TIERS[i + 1] - QTY_TIERS[i]);
        return QTY_MULTS[i] + t * (QTY_MULTS[i + 1] - QTY_MULTS[i]);
      }
    }
    return QTY_MULTS[last];
  }

  function rawFormula(w, h, qty) {
    var m = qtyMultiplier(qty);
    if (w > BIG_THRESH || h > BIG_THRESH) m *= BIG_BUMP;
    return PRICE_BASE * Math.pow((w * h) / 9, PRICE_EXP) * m;
  }

  // Standard-matrix price at exact width/height/qty (whole-dollar .99 ending).
  function standardPrice(w, h, qty) {
    return round2(Math.round(rawFormula(w, h, qty)) - 0.01);
  }

  /* ---- Approved explicit OVERRIDE table (always wins over the formula) ----
     Square sizes, at the comparison-heavy quantities. Prices are final USD.
     Do NOT extrapolate these to 200/500/1000 — those stay on the formula. */
  var OVERRIDES = {
    7:  { 10: 49.99, 25: 64.99, 50: 119.99, 100: 199.99 },
    8:  { 10: 54.99, 25: 74.99, 50: 139.99, 100: 239.99 },
    9:  { 10: 59.99, 25: 84.99, 50: 159.99, 100: 279.99 },
    10: { 10: 64.99, 25: 94.99, 50: 179.99, 100: 319.99 }
  };

  /* ---- Future PROMOTIONAL overrides (highest precedence; empty at launch) --
     Same shape as OVERRIDES: { sizeInches: { qty: price } }. When a promo price
     exists for a (size, qty) it wins over both the override table and the formula. */
  var PROMO = {};

  /* ---- Sticker Sheets — priced as die-cut × 0.92 (Launch V1 · D-033) -------
     A Sticker Sheet costs exactly 8% less than the equivalent die-cut order. This is
     DERIVED from `diecut()` (see `sheet()` below) — there is NO separate sheet table
     and NO second pricing model. Die-cut stays the single source of truth. */
  var SHEET_FACTOR = 0.92;   // sheet = die-cut × 0.92  (8% less — D-033)
  var SHEETS = {
    production: true,
    status: 'live',
    priced: true,
    factor: SHEET_FACTOR,
    note: 'Sticker Sheet price = die-cut price × ' + SHEET_FACTOR + ' (Launch V1, D-033). ' +
          'Derived from the die-cut engine — single source of truth; no separate sheet table.'
  };

  /* ---- helpers ---------------------------------------------------------- */
  function round2(n) { return Math.round((n + Number.EPSILON) * 100) / 100; }
  // Rule 1: always two decimals.
  function format(n) { return round2(n).toFixed(2); }

  // An override/promo applies to an approximately-square sticker whose size rounds
  // to an integer S that exists in the table. Non-square stickers stay on the formula.
  function squareSizeKey(w, h) {
    var sw = Math.round(w), sh = Math.round(h);
    return sw === sh ? sw : null;
  }
  function tableFor(size) {
    if (size == null) return null;
    return { promo: PROMO[size] || null, override: OVERRIDES[size] || null };
  }

  // Resolve the price + source at a single approved quantity tier for this size.
  function tierPrice(w, h, size, tier) {
    var t = tableFor(size);
    if (t && t.promo && t.promo[tier] != null)     return { price: round2(t.promo[tier]),     source: 'promo' };
    if (t && t.override && t.override[tier] != null) return { price: round2(t.override[tier]), source: 'override' };
    return { price: standardPrice(w, h, tier), source: 'standard' };
  }

  /* ---- public: die-cut price for a build --------------------------------
     diecut(widthIn, heightIn, qty) -> {
       price, perUnit, priceText, perUnitText, currency, size, qty, source
     }  or  { error: 'invalid-size' | 'invalid-quantity' }.
     Options (material/laminate/border/proof/notes) are intentionally not parameters:
     they do not affect price (Rule 4). */
  function diecutCalculated(widthIn, heightIn, qty) {
    var w = Number(widthIn), h = Number(heightIn), q = Math.round(Number(qty));
    if (!isFinite(w) || !isFinite(h) || w <= 0 || h <= 0) return { error: 'invalid-size' };
    if (!isFinite(q) || q < QTY_MIN || q > QTY_MAX)       return { error: 'invalid-quantity' };

    var size = squareSizeKey(w, h);           // integer size if square, else null
    var hasTable = size != null && (OVERRIDES[size] || PROMO[size]);

    var price, source;
    if (!hasTable) {
      // Pure standard matrix — exact at any qty (unchanged existing behavior).
      price = standardPrice(w, h, q);
      source = 'standard';
    } else {
      // Override/promo size: resolve each tier (override/promo/formula), then either
      // return the exact tier or interpolate between adjacent tier prices for a custom
      // qty. Interpolating the resolved TABLE prices keeps the curve smooth (no cliffs)
      // without exposing the formula multipliers.
      var exact = QTY_TIERS.indexOf(q);
      if (exact !== -1) {
        var tp = tierPrice(w, h, size, q);
        price = tp.price; source = tp.source;
      } else if (q <= QTY_TIERS[0]) {
        var lo = tierPrice(w, h, size, QTY_TIERS[0]); price = lo.price; source = lo.source;
      } else if (q >= QTY_TIERS[QTY_TIERS.length - 1]) {
        var hi = tierPrice(w, h, size, QTY_TIERS[QTY_TIERS.length - 1]); price = hi.price; source = hi.source;
      } else {
        for (var i = 0; i < QTY_TIERS.length - 1; i++) {
          var a = QTY_TIERS[i], b = QTY_TIERS[i + 1];
          if (q >= a && q <= b) {
            var pa = tierPrice(w, h, size, a).price, pb = tierPrice(w, h, size, b).price;
            var frac = (q - a) / (b - a);
            price = round2(Math.round(pa + frac * (pb - pa)) - 0.01);
            source = 'interpolated';
            break;
          }
        }
      }
    }

    var perUnit = round2(price / q);
    return {
      price: round2(price),
      perUnit: perUnit,
      priceText: '$' + format(price),
      perUnitText: '$' + format(perUnit),
      currency: 'USD',
      size: size,      // integer size if square, else null (priced by area)
      qty: q,
      source: source
    };
  }

  /* ---- public: die-cut price with the per-design minimum ----------------
     The calculated price, or MIN_DESIGN_PRICE when that is greater. The result
     says so: source 'minimum', calculatedPrice = what the formula/table gave,
     minimumApplied = true. perUnit is the charged price over the real count. */
  function diecut(widthIn, heightIn, qty) {
    var r = diecutCalculated(widthIn, heightIn, qty);
    if (r.error || r.price >= MIN_DESIGN_PRICE) return r;
    var price = MIN_DESIGN_PRICE, perUnit = round2(price / r.qty);
    return {
      price: price,
      perUnit: perUnit,
      priceText: '$' + format(price),
      perUnitText: '$' + format(perUnit),
      currency: 'USD',
      size: r.size,
      qty: r.qty,
      source: 'minimum',
      calculatedPrice: r.price,
      minimumApplied: true
    };
  }

  /* ---- public: Sticker Sheet price (Launch V1 · D-033) ------------------
     sheet(widthIn, heightIn, qty) = diecut(...) × 0.92, DERIVED from the die-cut
     engine (single source of truth — no separate table). Preserves every die-cut
     quantity break, size calculation, override/promo, and rounding: it takes the
     final die-cut price and applies the 8%-less multiplier, then rounds to cents.
     Returns the same shape as diecut() plus { diecutPrice, factor }, or { error }. */
  function sheet(widthIn, heightIn, qty) {
    /* the CALCULATED die-cut price: the per-design minimum is for individual
       stickers and never reaches a sheet (Ryan, 24 Oct 2026) */
    var dc = diecutCalculated(widthIn, heightIn, qty);
    if (dc.error) return { error: dc.error };
    var price = round2(dc.price * SHEET_FACTOR);   // exactly 8% less than die-cut
    var perUnit = round2(price / dc.qty);
    return {
      price: price,
      perUnit: perUnit,
      priceText: '$' + format(price),
      perUnitText: '$' + format(perUnit),
      currency: 'USD',
      size: dc.size,
      qty: dc.qty,
      source: dc.source,        // inherits the die-cut source (standard/override/promo/interpolated)
      diecutPrice: dc.price,    // the canonical die-cut price this is derived from
      factor: SHEET_FACTOR      // 0.92
    };
  }

  /* ---- public API -------------------------------------------------------- */
  var API = {
    version: 'diecut-v1',
    diecut: diecut,
    sheet: sheet,          // Sticker Sheet price = die-cut × 0.92 (D-033)
    minDesignPrice: MIN_DESIGN_PRICE,   // individual stickers, per design (24 Oct 2026)
    format: format,
    tiers: QTY_TIERS.slice(),
    sheets: SHEETS,        // live; see .note
    // exposed for tests / tooling — not for customer display:
    _standardPrice: standardPrice,
    _diecutCalculated: diecutCalculated,
    _overrides: OVERRIDES,
    _promo: PROMO
  };

  if (typeof module !== 'undefined' && module.exports) module.exports = API;
  if (root) root.SLPricing = API;
})(typeof window !== 'undefined' ? window : null);
